import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { SNAKE_CONFIG } from './snakeConfig.js';
import { drawBush, drawRock, drawTree } from '../../ui/Decor.js';

const INSET = 3;

/**
 * Obstacle types that get a gentle "wind sway" — anything that's a
 * living plant. Lamps, buildings, stones, benches, signs and vehicles
 * stay rock-still.
 */
const SWAY_TYPES = new Set(['tree', 'bush', 'flowerbed']);

/**
 * Sway amplitude range in radians (≈ ±1.5° to ±3.5°). Per-item value is
 * picked once at render so each plant moves at a slightly different
 * strength — keeps the field from looking mechanically synchronised.
 */
const SWAY_AMP_MIN = 0.025;
const SWAY_AMP_MAX = 0.060;

/**
 * Sway period range in milliseconds (≈ 2.4 s – 3.8 s for a full cycle).
 * Wider variance reads more like an organic breeze than a metronome.
 */
const SWAY_PERIOD_MIN = 2400;
const SWAY_PERIOD_MAX = 3800;

/**
 * Random obstacle manager for the Snake board.
 * Supports single-cell (tree, bush, rock) and multi-cell (building) obstacles.
 * Collision lookup is O(1) via a Set of "col,row" keys.
 */
export class SnakeObstacles {
    container = new Container();

    /** @type {Set<string>} "col,row" keys for occupied cells */
    _cells = new Set();

    /** @type {{ col: number, row: number, type: string, slot?: string, color: number, shape: string, w: number, h: number }[]} */
    _items = [];

    /** Node references for viewport culling */
    _nodes = [];

    _themeManager = null;

    constructor(themeManager = null) {
        this._themeManager = themeManager;
    }

    /**
     * Generate `count` obstacles randomly placed on the grid.
     * @param {number} cols
     * @param {number} rows
     * @param {Set<string>} excluded — "col,row" keys to avoid
     * @param {number} count
     */
    generate(cols, rows, excluded, count) {
        this.clear();

        const types = SNAKE_CONFIG.OBSTACLE_TYPES;
        /** Number of times each slot has already been placed this call. */
        const slotCount = new Map();
        /**
         * Pool of templates we still want to pick before allowing any
         * repeats. We seed it with every template once, so the first pass
         * spawns at most one of each slot — that maximises visual variety
         * on every board.
         *
         * Once the pool is empty (the player asked for more obstacles than
         * we have unique slots, or several picks landed on impossible
         * footprints), we fall back to the full template list and allow
         * repeats. Templates with an explicit `maxPerBoard` cap are
         * still honoured during the repeat phase.
         */
        const pool = types.slice();

        let placed = 0;
        let attempts = 0;
        const maxAttempts = count * 30;

        while (placed < count && attempts < maxAttempts) {
            attempts++;

            let tpl;
            let pickedFromPool = false;
            if (pool.length > 0) {
                const idx = Math.floor(Math.random() * pool.length);
                tpl = pool[idx];
                pickedFromPool = true;
            } else {
                tpl = types[Math.floor(Math.random() * types.length)];
                if (tpl.maxPerBoard != null && tpl.slot) {
                    const c = slotCount.get(tpl.slot) || 0;
                    if (c >= tpl.maxPerBoard) continue;
                }
            }

            const col = Math.floor(Math.random() * (cols - tpl.w + 1));
            const row = Math.floor(Math.random() * (rows - tpl.h + 1));

            let fits = true;
            for (let dc = 0; dc < tpl.w && fits; dc++) {
                for (let dr = 0; dr < tpl.h && fits; dr++) {
                    const key = `${col + dc},${row + dr}`;
                    if (excluded.has(key) || this._cells.has(key)) fits = false;
                }
            }
            if (!fits) continue;

            for (let dc = 0; dc < tpl.w; dc++) {
                for (let dr = 0; dr < tpl.h; dr++) {
                    this._cells.add(`${col + dc},${row + dr}`);
                }
            }

            this._items.push({
                col, row,
                type: tpl.type,
                slot: tpl.slot || null,
                color: tpl.color,
                shape: tpl.shape, w: tpl.w, h: tpl.h,
            });

            if (pickedFromPool) {
                const idx = pool.indexOf(tpl);
                if (idx >= 0) pool.splice(idx, 1);
            }
            if (tpl.slot) {
                slotCount.set(tpl.slot, (slotCount.get(tpl.slot) || 0) + 1);
            }
            placed++;
        }
    }

    /**
     * Sprinkle small natural decor (trees, bushes, flowerbeds) along the
     * playfield margins to give the level a populated edge without
     * crowding the playable centre. Items go through the SAME collision
     * set as primary obstacles — they kill the snake just like any other
     * obstacle, they're just biased toward the outer band of the grid.
     *
     * Called AFTER {@link generate} so the regular obstacle layout claims
     * the centre first, then this pass fills the leftover edges.
     *
     * @param {number} cols
     * @param {number} rows
     * @param {Set<string>} excluded — same exclusion set used by generate()
     * @param {number} count          — soft cap on how many extras to place
     * @param {number} [marginCells]  — depth of the "margin band" in cells
     */
    generateMarginDecor(cols, rows, excluded, count, marginCells = 3) {
        if (count <= 0 || marginCells <= 0) return;

        const decorTypes = SNAKE_CONFIG.OBSTACLE_TYPES.filter((t) =>
            (t.type === 'tree' || t.type === 'bush' || t.type === 'flowerbed') &&
            t.w <= 2 && t.h <= 2
        );
        if (decorTypes.length === 0) return;

        // Build candidate cells from the four margin bands. Corners are
        // covered by the top/bottom passes — the left/right passes skip
        // the corner rows to avoid duplicates.
        const positions = [];
        for (let row = 0; row < Math.min(marginCells, rows); row++) {
            for (let col = 0; col < cols; col++) positions.push({ col, row });
        }
        for (let row = Math.max(0, rows - marginCells); row < rows; row++) {
            for (let col = 0; col < cols; col++) positions.push({ col, row });
        }
        for (let row = marginCells; row < rows - marginCells; row++) {
            for (let col = 0; col < Math.min(marginCells, cols); col++) {
                positions.push({ col, row });
            }
            for (let col = Math.max(0, cols - marginCells); col < cols; col++) {
                positions.push({ col, row });
            }
        }

        // Fisher–Yates shuffle so adjacent items don't all clump on one side.
        for (let i = positions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [positions[i], positions[j]] = [positions[j], positions[i]];
        }

        let placed = 0;
        for (const pos of positions) {
            if (placed >= count) break;
            const tpl = decorTypes[Math.floor(Math.random() * decorTypes.length)];
            const { col, row } = pos;
            if (col + tpl.w > cols || row + tpl.h > rows) continue;

            let fits = true;
            for (let dc = 0; dc < tpl.w && fits; dc++) {
                for (let dr = 0; dr < tpl.h && fits; dr++) {
                    const key = `${col + dc},${row + dr}`;
                    if (excluded.has(key) || this._cells.has(key)) fits = false;
                }
            }
            if (!fits) continue;

            for (let dc = 0; dc < tpl.w; dc++) {
                for (let dr = 0; dr < tpl.h; dr++) {
                    this._cells.add(`${col + dc},${row + dr}`);
                }
            }
            this._items.push({
                col, row,
                type: tpl.type,
                slot: tpl.slot || null,
                color: tpl.color,
                shape: tpl.shape, w: tpl.w, h: tpl.h,
            });
            this._dirty = true;
            placed++;
        }
    }

    /** @returns {boolean} */
    occupies(col, row) {
        return this._cells.has(`${col},${row}`);
    }

    /**
     * Snapshot of every current obstacle in world-space coordinates,
     * using the origin / cellSize from the most recent {@link render}
     * call. Returned items include the source fields (col/row/w/h/type/
     * color/shape) plus a precomputed `centerX/centerY` so callers can
     * fire per-obstacle effects (e.g. one confetti burst per item) and
     * then call {@link clear} to wipe the board.
     *
     * @returns {Array<{
     *     col: number, row: number, w: number, h: number,
     *     type: string, color: number, shape: string,
     *     centerX: number, centerY: number
     * }>}
     */
    getItemsWorld() {
        const cs = this._lastCs || 0;
        const ox = this._lastOx || 0;
        const oy = this._lastOy || 0;
        return this._items.map((it) => ({
            ...it,
            centerX: ox + (it.col + it.w / 2) * cs,
            centerY: oy + (it.row + it.h / 2) * cs,
        }));
    }

    /**
     * Remove the obstacle that includes (col, row). Multi-cell obstacles
     * (e.g. a 2×2 building) are removed as a whole — hitting any of their
     * cells destroys the entire footprint.
     *
     * @returns {null | {
     *     col: number, row: number, w: number, h: number,
     *     type: string, color: number, shape: string,
     *     centerX: number, centerY: number
     * }}
     *   the removed item enriched with its world-space centre (ox/oy/cs
     *   pulled from the last render call), or `null` if no obstacle was
     *   hit. The centre is convenient for callers that want to emit a
     *   confetti burst at the obstacle's middle.
     */
    /**
     * Returns the rendered Pixi `Container` for the obstacle that
     * occupies (col, row), or `null` if none. Caller can mutate the
     * node's `alpha` / `tint` to highlight the obstacle without
     * removing it — used by the game-over freeze in SnakeScene to
     * blink the killer obstacle for 3 seconds before the overlay
     * appears.
     */
    nodeAt(col, row) {
        const idx = this._items.findIndex((o) =>
            col >= o.col && col < o.col + o.w &&
            row >= o.row && row < o.row + o.h
        );
        if (idx < 0) return null;
        return this._nodes[idx]?.node || null;
    }

    removeAt(col, row) {
        const idx = this._items.findIndex((o) =>
            col >= o.col && col < o.col + o.w &&
            row >= o.row && row < o.row + o.h
        );
        if (idx < 0) return null;

        const item = this._items[idx];
        for (let dc = 0; dc < item.w; dc++) {
            for (let dr = 0; dr < item.h; dr++) {
                this._cells.delete(`${item.col + dc},${item.row + dr}`);
            }
        }
        this._items.splice(idx, 1);
        this._dirty = true;

        const cs = this._lastCs || 0;
        const ox = this._lastOx || 0;
        const oy = this._lastOy || 0;
        return {
            ...item,
            centerX: ox + (item.col + item.w / 2) * cs,
            centerY: oy + (item.row + item.h / 2) * cs,
        };
    }

    _dirty = true;
    _lastOx = 0;
    _lastOy = 0;
    _lastCs = 0;

    /**
     * Render all obstacles. Caches the result — only rebuilds when
     * items changed (generate/clear) or grid parameters changed.
     */
    render(ox, oy, cs) {
        if (!this._dirty && ox === this._lastOx && oy === this._lastOy && cs === this._lastCs) {
            return;
        }
        this._dirty = false;
        this._lastOx = ox;
        this._lastOy = oy;
        this._lastCs = cs;

        this.container.removeChildren();
        this._nodes.length = 0;

        for (const obs of this._items) {
            const x = ox + obs.col * cs;
            const y = oy + obs.row * cs;
            const w = obs.w * cs;
            const h = obs.h * cs;
            const node = new Container();
            const g = new Graphics();

            // Plants get an inner "sway" container pivoted at the base
            // of the obstacle so a small rotation reads as a wind-bent
            // top while the trunk/stem stays planted. Everything else
            // is added directly to `node` and never rotates.
            const swayable = SWAY_TYPES.has(obs.type);
            let drawTarget = node;
            let sway = null;
            if (swayable) {
                sway = new Container();
                // Pivot at the bottom-centre of the obstacle's footprint
                // in WORLD coords — children are drawn at world coords
                // (the sprite is positioned at world centre below, the
                // fallback graphics are drawn in world space) so the
                // pivot must match.
                const pivotX = x + w / 2;
                const pivotY = y + h;
                sway.pivot.set(pivotX, pivotY);
                sway.x = pivotX;
                sway.y = pivotY;
                node.addChild(sway);
                drawTarget = sway;
            }

            const sprite = this._themeManager?.createSprite(
                this._getThemeSlotKey(obs),
                w,
                h,
                { fitMode: 'stretch' }
            );
            if (sprite) {
                // PNG provided — render it cleanly on the transparent
                // arena background. No base plate, no shadow, no inset:
                // the artwork's own transparency carries the silhouette.
                // `stretch` fit makes the sprite fill the cell footprint
                // exactly so a square source PNG in a 1×2 footprint (e.g.
                // the small tree) takes both cells instead of letterboxing.
                sprite.x = x + w / 2;
                sprite.y = y + h / 2;
                drawTarget.addChild(sprite);
            } else {
                switch (obs.type) {
                    case 'tree':
                    case 'lamp':
                        this._drawTree(g, x, y, w, h);
                        break;
                    case 'bush':
                    case 'flowerbed':
                        this._drawBush(g, x, y, w, h);
                        break;
                    case 'rock':
                        this._drawRock(g, x, y, w, h);
                        break;
                    case 'bench':
                    case 'barrier':
                    case 'sign':
                    case 'truck':
                    case 'building':
                    default:
                        this._drawBuilding(g, x, y, w, h);
                        break;
                }
                drawTarget.addChild(g);
            }

            const entry = { node, x, y, w, h };
            if (sway) {
                entry.sway = sway;
                entry.swayPhase = Math.random() * Math.PI * 2;
                entry.swayPeriod = SWAY_PERIOD_MIN
                    + Math.random() * (SWAY_PERIOD_MAX - SWAY_PERIOD_MIN);
                entry.swayAmp = SWAY_AMP_MIN
                    + Math.random() * (SWAY_AMP_MAX - SWAY_AMP_MIN);
            }
            this._nodes.push(entry);
            this.container.addChild(node);
        }
    }

    /**
     * Apply a gentle wind sway to every flora obstacle (trees, bushes,
     * flowerbeds). Each plant has its own random phase / period /
     * amplitude so they don't move in lockstep — the field looks like
     * it's catching a real breeze rather than a synchronised wave.
     *
     * Call once per frame. Safe to call before any obstacle has been
     * rendered. Has no effect on rocks, lamps, buildings, etc.
     */
    sway() {
        if (this._nodes.length === 0) return;
        const now = Date.now();
        for (const entry of this._nodes) {
            if (!entry.sway || entry.sway.destroyed) continue;
            const t = (now * (2 * Math.PI / entry.swayPeriod)) + entry.swayPhase;
            entry.sway.rotation = Math.sin(t) * entry.swayAmp;
        }
    }

    /**
     * Viewport culling — hide obstacles that are off-screen.
     * Call each frame when the camera moves (Free Roam).
     * @param {number} vpX — viewport left in world coords
     * @param {number} vpY — viewport top in world coords
     * @param {number} vpW — viewport width
     * @param {number} vpH — viewport height
     */
    cull(vpX, vpY, vpW, vpH) {
        const margin = 40;
        const left = vpX - margin;
        const right = vpX + vpW + margin;
        const top = vpY - margin;
        const bottom = vpY + vpH + margin;

        for (const entry of this._nodes) {
            const visible = entry.x + entry.w > left && entry.x < right &&
                            entry.y + entry.h > top && entry.y < bottom;
            entry.node.visible = visible;
        }
    }

    _drawBase(g, x, y, w, h) {
        g.ellipse(x + w / 2 + 2, y + h - INSET + 2, w * 0.4, h * 0.08);
        g.fill({ color: 0x000000, alpha: 0.3 });

        g.roundRect(x + INSET, y + INSET, w - INSET * 2, h - INSET * 2, 8);
        g.fill({ color: 0x0f172a, alpha: 0.85 });
        g.stroke({ color: 0x334155, width: 1.5 });
    }

    _drawTree(g, x, y, w, h) {
        this._drawBase(g, x, y, w, h);
        drawTree(g, x, y, w, h, { shadow: false });
    }

    _drawBush(g, x, y, w, h) {
        this._drawBase(g, x, y, w, h);
        drawBush(g, x, y, w, h, { shadow: false });
    }

    _drawRock(g, x, y, w, h) {
        this._drawBase(g, x, y, w, h);
        drawRock(g, x, y, w, h);
    }

    _drawBuilding(g, x, y, w, h) {
        this._drawBase(g, x, y, w, h);

        const bx = x + w * 0.14;
        const by = y + h * 0.28;
        const bw = w * 0.72;
        const bh = h * 0.58;

        g.roundRect(bx, by, bw, bh, 3);
        g.fill(0x475569);
        g.roundRect(bx, by, bw * 0.3, bh, 3);
        g.fill({ color: 0x546478, alpha: 0.4 });

        g.roundRect(bx, by, bw, bh * 0.08, 3);
        g.fill({ color: 0x64748b, alpha: 0.5 });

        const roofY = by - h * 0.04;
        g.roundRect(bx - w * 0.02, roofY, bw + w * 0.04, h * 0.06, 2);
        g.fill(0x64748b);

        g.roundRect(x + w * 0.4, by + bh * 0.6, w * 0.2, bh * 0.4, 2);
        g.fill(0x5c3a1e);
        g.roundRect(x + w * 0.4, by + bh * 0.6, w * 0.08, bh * 0.4, 2);
        g.fill({ color: 0x78500f, alpha: 0.35 });
        g.circle(x + w * 0.56, by + bh * 0.78, w * 0.02);
        g.fill(0xfbbf24);

        const winColor = 0x93c5fd;
        const winGlow = { color: 0xbfdbfe, alpha: 0.35 };
        const winW = w * 0.13;
        const winH = h * 0.1;
        const wy = by + bh * 0.2;

        g.roundRect(x + w * 0.2, wy, winW, winH, 2);
        g.fill(winColor);
        g.roundRect(x + w * 0.2, wy, winW * 0.45, winH, 2);
        g.fill(winGlow);

        g.roundRect(x + w * 0.67, wy, winW, winH, 2);
        g.fill(winColor);
        g.roundRect(x + w * 0.67, wy, winW * 0.45, winH, 2);
        g.fill(winGlow);

        const wy2 = by + bh * 0.42;
        g.roundRect(x + w * 0.2, wy2, winW, winH, 2);
        g.fill(winColor);
        g.roundRect(x + w * 0.2, wy2, winW * 0.45, winH, 2);
        g.fill(winGlow);

        g.roundRect(x + w * 0.67, wy2, winW, winH, 2);
        g.fill(winColor);
        g.roundRect(x + w * 0.67, wy2, winW * 0.45, winH, 2);
        g.fill(winGlow);
    }

    _getThemeSlotKey(obs) {
        // Explicit slot from OBSTACLE_TYPES template wins — this is how
        // variant art (pine tree, pink bush, hospital, etc.) is selected.
        // The fallback below covers legacy generators that don't set a
        // slot; all fallback slot keys point at art that actually ships
        // in `assets/snake/` (no orphan paths).
        if (obs.slot) return `snake.${obs.slot}`;
        if (obs.type === 'tree') {
            return obs.h >= 4 ? 'snake.obstacle.tree.pine' : 'snake.obstacle.tree.single';
        }
        if (obs.type === 'bush') {
            return obs.w >= 4 ? 'snake.obstacle.bush.pink' : 'snake.obstacle.bush.single';
        }
        if (obs.type === 'rock') {
            return obs.w >= 3 ? 'snake.obstacle.rock.large' : 'snake.obstacle.rock.single';
        }
        if (obs.type === 'flowerbed') return 'snake.obstacle.flowerbed';
        if (obs.type === 'lamp')      return 'snake.obstacle.lamp';
        if (obs.type === 'bench')     return 'snake.obstacle.bench';
        if (obs.type === 'barrier')   return 'snake.obstacle.barrier';
        if (obs.type === 'sign')      return 'snake.obstacle.stop';
        if (obs.type === 'truck')     return 'snake.obstacle.icecream_truck';
        return 'snake.obstacle.building.large';
    }

    clear() {
        this.container.removeChildren();
        this._items.length = 0;
        this._nodes.length = 0;
        this._cells.clear();
        this._dirty = true;
    }

    destroy() {
        this.clear();
    }
}
